import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonList,
    IonAlert,
    IonModal,
} from "@ionic/react";

interface QuestionData {
    word: string;
    options: string[];
    correctAnswer: string;
}


const RhymingWords: React.FC = () => {

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctAnswer) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'RhymingWords.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/RhymingWords.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Rhyming Words Worksheets for UKG | Fun English Activities for Kids"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Explore engaging rhyming word activities for UKG students at Mama’s Guidance to boost phonemic awareness and make early reading fun and interactive.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Rhyming Words</h1>

                                <p>
                                    <strong>Rhyming words</strong> are words that sound alike at the end, like <em>cat-hat</em> or <em>sun-fun</em>. They help children recognize sound patterns, build early reading confidence, and expand vocabulary. Our <strong>UKG rhyming word worksheets</strong> include colorful pictures, matching games, and engaging exercises perfect for 4–6 year olds.
                                </p>

                                <h2>🎯 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Identify pairs of rhyming words through pictures and sounds</li>
                                    <li>Match words that sound similar</li>
                                    <li>Build early reading and listening skills</li>
                                    <li>Understand word families (<strong>e.g., -at, -an, -og</strong>)</li>
                                </ul>

                                <h2>📋 Activities in the Worksheets</h2>
                                <ul>
                                    <li>Circle the pair of rhyming words</li>
                                    <li>Match pictures that rhyme</li>
                                    <li>Choose the rhyming word from options</li>
                                    <li>Color the words that rhyme</li>
                                    <li>Rhyming word puzzles and picture identification</li>
                                </ul>

                                <h2>👨‍👩‍👧 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Read rhyming books like Dr. Seuss to your child</li>
                                    <li>Sing rhyming songs and nursery rhymes together</li>
                                    <li>Play <strong>“What Rhymes With…”</strong> games at home</li>
                                    <li>Use flashcards or toys to build rhyming word families</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/articles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Articles</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/opposites"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Opposites</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Play a rhyming scavenger hunt: Ask your child to find things at home that rhyme (<strong>e.g., a sock and a rock</strong>). This makes learning feel like play!
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Rhyming Words</h1>

                            <p>
                                <strong>Rhyming words</strong> are words that sound alike at the end, like <em>cat-hat</em> or <em>sun-fun</em>. They help children recognize sound patterns, build early reading confidence, and expand vocabulary. Our <strong>UKG rhyming word worksheets</strong> include colorful pictures, matching games, and engaging exercises perfect for 4–6 year olds.
                            </p>

                            <h2>🎯 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Identify pairs of rhyming words through pictures and sounds</li>
                                <li>Match words that sound similar</li>
                                <li>Build early reading and listening skills</li>
                                <li>Understand word families (<strong>e.g., -at, -an, -og</strong>)</li>
                            </ul>

                            <h2>📋 Activities in the Worksheets</h2>
                            <ul>
                                <li>Circle the pair of rhyming words</li>
                                <li>Match pictures that rhyme</li>
                                <li>Choose the rhyming word from options</li>
                                <li>Color the words that rhyme</li>
                                <li>Rhyming word puzzles and picture identification</li>
                            </ul>

                            <h2>👨‍👩‍👧 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Read rhyming books like Dr. Seuss to your child</li>
                                <li>Sing rhyming songs and nursery rhymes together</li>
                                <li>Play <strong>“What Rhymes With…”</strong> games at home</li>
                                <li>Use flashcards or toys to build rhyming word families</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/opposites"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Opposites</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Play a rhyming scavenger hunt: Ask your child to find things at home that rhyme (<strong>e.g., a sock and a rock</strong>). This makes learning feel like play!
                            </p>
                        </article>
                        <p>
                            <strong>Find the word that rhymes with:</strong>{" "}
                            <h3>{questions[currentIndex]?.word}</h3>
                        </p>

                        <IonRadioGroup
                            value={selectedOption}
                            onIonChange={(e) => handleSubmit(e.detail.value!)}
                        >
                            <IonList className="grid-item-container">
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem
                                        className={`grid-item ${selectedOption === option
                                            ? option === questions[currentIndex]?.correctAnswer
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}
                                        key={index}>
                                        <IonLabel>{option}</IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonList>
                        </IonRadioGroup>

                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default RhymingWords;
